import { useState, useEffect } from 'react';
import { useAuth } from '../../lib/auth/AuthContext';
import { AuthMethod } from '../../lib/storage/types';
import {
  useCurrentWallet,
  useCurrentAddress,
  useWallets,
  useConnectWallet,
} from '@roochnetwork/rooch-sdk-kit';

interface WalletLoginProps {
  onSuccess?: (userDid: string, isNew: boolean) => void;
  onError?: (error: string) => void;
}

export function WalletLogin({ onSuccess, onError }: WalletLoginProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [hookError, setHookError] = useState<string | null>(null);
  const { loginWithProvider } = useAuth();

  // Rooch SDK Kit hooks - these might fail if wallet environment is not properly set up
  const currentWallet = useCurrentWallet();
  const currentAddress = useCurrentAddress();
  const wallets = useWallets();
  const { mutateAsync: connectWallet } = useConnectWallet();

  // Handle wallet connection state changes
  useEffect(() => {
    try {
      if (currentWallet?.isConnected && currentAddress) {
        handleWalletConnected();
      }
    } catch (error) {
      console.error('[WalletLogin] Error in wallet connection effect:', error);
      setHookError(error instanceof Error ? error.message : 'Wallet connection error');
    }
  }, [currentWallet?.isConnected, currentAddress]);

  const handleWalletConnected = async () => {
    if (!currentAddress) {
      onError?.('No wallet address found');
      return;
    }

    try {
      setIsLoading(true);

      // Use the new loginWithProvider method
      const result = await loginWithProvider(AuthMethod.WALLET);

      onSuccess?.(result.userDid, result.isNewUser);
    } catch (error) {
      console.error('Wallet authentication failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Wallet authentication failed';
      onError?.(errorMessage);

      // Note: No disconnect method available in current SDK version
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectWallet = async () => {
    try {
      // Check for hook errors first
      if (hookError) {
        onError?.(hookError);
        return;
      }

      // Check if wallets array is available
      if (!wallets || wallets.length === 0) {
        const errorMessage =
          'No wallet providers available. Please ensure you have Bitcoin wallet extensions installed (UniSat, OKX, or OneKey).';
        onError?.(errorMessage);
        return;
      }

      if (currentWallet?.isConnected) {
        // Already connected, proceed with authentication
        await handleWalletConnected();
        return;
      }

      setIsLoading(true);

      // Filter out problematic wallets first
      const safeWallets = wallets.filter(wallet => {
        const walletName = wallet.getName();
        // Filter out LocalWallet which has getBitcoinAddressWith issues
        if (walletName === 'Local Wallet' || walletName === 'LocalWallet') {
          console.warn(`[WalletLogin] Filtering out problematic wallet: ${walletName}`);
          return false;
        }
        return true;
      });

      // Get available wallets - check installation status with error handling
      let walletInstallationChecks;
      try {
        walletInstallationChecks = await Promise.all(
          safeWallets.map(async wallet => {
            try {
              return {
                wallet,
                isInstalled: await wallet.checkInstalled(),
              };
            } catch (error) {
              console.warn(`[WalletLogin] Error checking wallet ${wallet.getName()}:`, error);
              return {
                wallet,
                isInstalled: false,
              };
            }
          })
        );
      } catch (error) {
        console.error('[WalletLogin] Error checking wallet installations:', error);
        onError?.('Failed to check wallet installations. Please refresh and try again.');
        setIsLoading(false);
        return;
      }

      const availableWallets = walletInstallationChecks
        .filter(({ isInstalled }) => isInstalled)
        .map(({ wallet }) => wallet);

      if (availableWallets.length === 0) {
        // In development, show more helpful error message
        const isDev = import.meta.env.DEV;
        const walletStatusList = walletInstallationChecks
          .map(
            ({ wallet, isInstalled }) =>
              `${wallet.getName()}(${isInstalled ? 'installed' : 'not installed'})`
          )
          .join(', ');

        const errorMessage = isDev
          ? `No Bitcoin wallets found. Available wallets: ${walletStatusList}. For development, you can install UniSat or OKX wallet, or we can add a mock wallet.`
          : 'No Bitcoin wallets found. Please install UniSat or OKX wallet to continue.';
        onError?.(errorMessage);
        setIsLoading(false);
        return;
      }

      // Connect to the first available wallet
      // In a real implementation, you might want to show a wallet selection dialog
      const walletToConnect = availableWallets[0];

      try {
        await connectWallet({ wallet: walletToConnect });
        // The useEffect will handle the rest when connection is established
      } catch (connectError) {
        console.error('[WalletLogin] Wallet connection failed:', connectError);

        // Handle specific LocalWallet getBitcoinAddressWith error
        const errorMessage =
          connectError instanceof Error ? connectError.message : 'Failed to connect wallet';
        if (
          errorMessage.includes('getBitcoinAddressWith') ||
          errorMessage.includes('this.keypair')
        ) {
          const friendlyMessage = `Wallet connection failed due to a compatibility issue. Please try installing a Bitcoin wallet extension like UniSat or OKX Wallet.`;
          onError?.(friendlyMessage);
        } else {
          onError?.(errorMessage);
        }
        setIsLoading(false);
      }
    } catch (error) {
      console.error('[WalletLogin] Unexpected error in handleConnectWallet:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unexpected wallet connection error';
      onError?.(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <button
        type="button"
        className="w-full flex justify-center items-center py-2 px-4 border border-orange-300 rounded-md shadow-sm text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={handleConnectWallet}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <svg
              className="animate-spin -ml-1 mr-3 h-4 w-4 text-orange-700"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Connecting...
          </>
        ) : (
          <>
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 6a2 2 0 114 0 2 2 0 01-4 0zm6 0a2 2 0 114 0 2 2 0 01-4 0z"
                clipRule="evenodd"
              />
            </svg>
            Connect Wallet
          </>
        )}
      </button>
    </div>
  );
}
